// brew-helpers.js — derived helpers for the coffee learning loop.
//
// Pure functions, no I/O. Composed by useStore and the Coffee module.

// Cup conventions.
//
// 150 g per cup and a 1–10 cup range are Fellow Aiden carafe conventions. They
// are a reasonable default for any drip brewer, but they are not universal: an
// AeroPress tops out around 2 cups, and a French press varies by vessel. Callers
// that know the user's brewer pass `limits`; everyone else gets the Aiden
// numbers, which is what this file always assumed.
const ML_PER_CUP = 150;
const AIDEN_LIMITS = { minCups: 1, maxCups: 10, batchFrom: 4 };

// Per-method overrides. Only what genuinely differs — anything absent falls
// back to the Aiden convention above.
export const METHOD_CUP_LIMITS = {
  drip:        { minCups: 1, maxCups: 10, batchFrom: 4 },
  pour_over:   { minCups: 1, maxCups: 8,  batchFrom: 4 },
  // An AeroPress brews one or two cups; "batch mode" has no meaning for it.
  aeropress:   { minCups: 1, maxCups: 2,  batchFrom: Infinity },
  french_press:{ minCups: 1, maxCups: 8,  batchFrom: 4 },
  // Espresso is measured in shots, not cups.
  espresso:    { minCups: 1, maxCups: 4,  batchFrom: Infinity },
};

export function cupLimits(method) {
  return METHOD_CUP_LIMITS[method?.id || method] || AIDEN_LIMITS;
}

// Cups are stored in 0.5 increments (1.0, 1.5, 2.0, …). The math is just
// water = cups * 150 g, clamped to what the brewer can actually hold.
export function cupsToWaterMl(cups, method = null) {
  const { minCups, maxCups } = cupLimits(method);
  const c = Math.max(minCups, Math.min(maxCups, Math.round((+cups || 0) * 2) / 2));
  return Math.round(c * ML_PER_CUP);
}

export function waterMlToCups(ml) {
  // Snap to nearest 0.5
  return Math.round(((ml || 0) / ML_PER_CUP) * 2) / 2;
}

// Single-serve vs batch. Only the Aiden actually has two modes; for brewers
// with no such split, batchFrom is Infinity so this is always false.
export function isBatchMode(cups, method = null) {
  return cups >= cupLimits(method).batchFrom;
}

// Grams-left planning: how many cups can the bag honestly cover?
//
// The morning ritual (docs/chatgpt-morning-coffee-transcript.md) plans around
// what's actually left — "46 g left → brew 6 cups well" — rather than
// stretching the full pour thin. Dose scales linearly with water
// (dose = water / ratio), so scale the known dose-at-`cups` down until it fits.
//
// Returns the largest whole cup count below `cups` whose dose fits
// `weightLeftG`, or null when there is nothing to scale: weight unknown, the
// full pour already fits, or even one cup doesn't fit.
export function maxCupsForWeight({ weightLeftG, doseG, cups, method = null }) {
  const left = +weightLeftG;
  if (weightLeftG == null || !Number.isFinite(left)) return null;
  if (!(+doseG > 0) || !(+cups > 1)) return null;
  if (left >= +doseG) return null; // full pour fits — nothing to do
  const waterAtCups = cupsToWaterMl(cups, method);
  if (!(waterAtCups > 0)) return null;
  // Start below both the requested cups AND the brewer's max — cupsToWaterMl
  // clamps to the brewer's range, so counting above it would "scale" to a cup
  // number with the identical (unaffordable) dose.
  const start = Math.min(Math.ceil(cups), cupLimits(method).maxCups + 1) - 1;
  for (let c = start; c >= 1; c--) {
    const dose = +doseG * (cupsToWaterMl(c, method) / waterAtCups);
    if (dose <= left + 0.5) return c; // half-gram grace for scale noise
  }
  return null;
}

// Hot vs cold-brew profile selection based on user pick + auto-bias from weather.
// hot mode picks a sensible default profile; cold always picks "cold-brew".
// suggested = a profileId the system thinks fits (from weather); pick = user override.
export function pickProfileForMode({ mode, suggestedProfileId, fallback = "balanced" }) {
  if (mode === "cold") return "cold-brew";
  return suggestedProfileId || fallback;
}

// Bag depletion forecast.
// Given a bean's bag size, weight remaining, and recent brews against it,
// return: brews_remaining, days_remaining (at current daily rate), should_reorder.
export function forecastDepletion(bean, brewLogs) {
  if (!bean) return null;
  const weightLeft = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  if (!weightLeft) return null;

  // Average dose per brew on this bean over the last 14 days
  const since = Date.now() - 14 * 86400 * 1000;
  const recent = (brewLogs || []).filter(
    l => l.bean_id === bean.id && new Date(l.brewed_at).getTime() >= since
  );

  let avgDose = 18;
  if (recent.length > 0) {
    avgDose = recent.reduce((s, l) => s + (+l.dose_g || 0), 0) / recent.length;
  }
  const brewsRemaining = Math.floor(weightLeft / avgDose);

  // Brews per day rate based on the last 14 days
  let brewsPerDay = 1;
  if (recent.length > 0) {
    brewsPerDay = recent.length / 14;
    if (brewsPerDay < 0.1) brewsPerDay = 0.1; // never project >140 days
  }
  const daysRemaining = brewsPerDay > 0 ? brewsRemaining / brewsPerDay : null;

  return {
    weight_left_g: weightLeft,
    weight_total_g: +(bean.weight_g ?? weightLeft),
    pct_remaining: Math.round((weightLeft / Math.max(1, +(bean.weight_g ?? weightLeft))) * 100),
    avg_dose_g: +avgDose.toFixed(1),
    brews_remaining: brewsRemaining,
    brews_per_day: +brewsPerDay.toFixed(2),
    days_remaining: daysRemaining ? Math.round(daysRemaining) : null,
    should_reorder: brewsRemaining <= 5,
  };
}

// Decrement bean weight when a brew is logged.
//
// `??` rather than `||` matters here: a bag decremented to exactly 0 has a
// FALSY weight_left_g, so `||` fell through to the bag's full weight_g and
// silently refilled it. Brewing 75 g from an empty 340 g bag left it reading
// 265 g — so an emptied bag came back to life every time it was used, and the
// depletion forecast never converged.
export function decrementBeanWeight(bean, doseG) {
  const current = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  const start = Number.isFinite(current) ? current : 0;
  const left = Math.max(0, start - (+doseG || 0));
  return { ...bean, weight_left_g: left };
}

/**
 * Grams left in a bag, or null when the weight was never recorded.
 *
 * The distinction matters in the UI: a bag imported from a photo whose label
 * had no net weight stores null, and rendering that as "0g left" made a full
 * bag look spent — identical to one actually finished. Callers should show
 * something like "weight not set" for null rather than a number.
 */
export function bagWeightLeft(bean) {
  const raw = bean?.weight_left_g ?? bean?.weight_g;
  if (raw == null || raw === "") return null;
  const n = +raw;
  return Number.isFinite(n) ? Math.round(n) : null;
}

// Bags physically available to brew from.
//
// A bag counts as active unless it is explicitly finished or known to be empty.
// Crucially, a bag whose weight was never recorded (bagWeightLeft === null) is
// NOT empty — we just don't know its weight — so it stays selectable. The old
// `weight > 0` test coerced that null to 0 and hid the bag, which is why an
// unweighed bag could not be chosen as the one in the container.
export function findActiveBags(beans) {
  return (beans || []).filter(b => {
    if (b.status === "finished") return false;
    const w = bagWeightLeft(b);
    return w == null || w > 0;
  });
}

// The bags currently OPEN — the ones actually available to brew (in Josh's
// vacuum container). He marks these explicitly via preferences.open_bean_ids.
// Until he ever does (ids undefined), fall back to "any bag with weight left"
// so the app still works out of the box. An explicit empty array means
// "nothing open" and is respected.
export function getOpenBeans(beans, preferences) {
  const ids = preferences?.open_bean_ids;
  const list = beans || [];
  if (Array.isArray(ids)) {
    const set = new Set(ids);
    return list.filter(b => set.has(b.id));
  }
  return findActiveBags(list);
}

// Canister true-up: net beans = gross scale reading − canister tare. Returns
// null if we can't compute (missing/invalid inputs) so callers can guard.
export function canisterNet(grossG, tareG) {
  // Reject missing values before coercing: +null and +"" are both 0, which is
  // finite, so the guard below would let them through and report an empty
  // canister where we actually have no reading at all.
  if (grossG == null || grossG === "" || tareG == null || tareG === "") return null;
  const g = +grossG, t = +tareG;
  if (!Number.isFinite(g) || !Number.isFinite(t)) return null;
  return Math.max(0, +(g - t).toFixed(1));
}

// Which bean is physically in the canister right now: the open bag Josh last
// brewed, else the first open bag. null if nothing is open.
export function canisterBean(beans, preferences) {
  const open = getOpenBeans(beans, preferences);
  if (!open.length) return null;
  return open.find(b => b.id === preferences?.last_bean_id) || open[0];
}

// Should this bean be reordered? True when it's below the canister's gram
// threshold, or (no threshold set) when the depletion forecast says so. Per
// product: each bean carries its own weight + trailing usage.
export function needsReorder(bean, forecast, thresholdG) {
  if (!bean) return false;
  const left = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  if (Number.isFinite(+thresholdG) && +thresholdG > 0) return left < +thresholdG;
  return !!forecast?.should_reorder;
}

// Roaster aggregate score from check-ins.
// Returns Map<roaster, {avg_rating, samples, top_origins[]}>
export function aggregateRoasterScores(beans, checkIns) {
  const beansById = new Map(beans.map(b => [b.id, b]));
  const byRoaster = new Map();

  for (const c of checkIns || []) {
    const bean = beansById.get(c.bean_id);
    const roaster = bean?.roaster?.trim();
    if (!roaster || c.rating == null) continue;

    if (!byRoaster.has(roaster)) {
      byRoaster.set(roaster, { ratings: [], origins: new Map() });
    }
    const agg = byRoaster.get(roaster);
    agg.ratings.push(+c.rating);

    for (const origin of bean.origin || []) {
      agg.origins.set(origin, (agg.origins.get(origin) || 0) + 1);
    }
  }

  const out = new Map();
  for (const [roaster, agg] of byRoaster.entries()) {
    const avg = agg.ratings.reduce((s, r) => s + r, 0) / agg.ratings.length;
    const top = [...agg.origins.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(e => e[0]);
    out.set(roaster, {
      avg_rating: +avg.toFixed(2),
      samples: agg.ratings.length,
      top_origins: top,
    });
  }
  return out;
}

// Score by origin (independent of roaster)
export function aggregateOriginScores(beans, checkIns) {
  const beansById = new Map(beans.map(b => [b.id, b]));
  const byOrigin = new Map();

  for (const c of checkIns || []) {
    const bean = beansById.get(c.bean_id);
    if (c.rating == null || !bean) continue;
    for (const origin of bean.origin || []) {
      if (!byOrigin.has(origin)) byOrigin.set(origin, []);
      byOrigin.get(origin).push(+c.rating);
    }
  }

  const out = new Map();
  for (const [origin, ratings] of byOrigin.entries()) {
    const avg = ratings.reduce((s, r) => s + r, 0) / ratings.length;
    out.set(origin, { avg_rating: +avg.toFixed(2), samples: ratings.length });
  }
  return out;
}

// When user adds a new bean, find their best matches from history.
export function insightForNewBean({ roaster, origin = [] }, roasterScores, originScores) {
  const lines = [];

  if (roaster && roasterScores.has(roaster)) {
    const r = roasterScores.get(roaster);
    if (r.samples >= 2) {
      lines.push({
        type: "roaster",
        text: `You've rated ${roaster} ${r.avg_rating.toFixed(1)}★ across ${r.samples} brews.`,
        score: r.avg_rating,
      });
    }
  }

  for (const o of origin) {
    if (originScores.has(o)) {
      const stats = originScores.get(o);
      if (stats.samples >= 2) {
        lines.push({
          type: "origin",
          text: `${o} beans average ${stats.avg_rating.toFixed(1)}★ for you (${stats.samples} brews).`,
          score: stats.avg_rating,
        });
      }
    }
  }

  return lines;
}
